"use client"

import type { ComponentType } from "react"

import { canonicalWalletQuery } from "@/features/wallet/route"
import { isModalDemoEnabled } from "@/utils/modal-demo"

export type ModalProps = {
  "sign-in": { redirect?: string }
  "sign-up": { redirect?: string; id_afiliado?: string; referral?: string }
  // Plain strings, not the WalletTab / method unions: UrlModal casts the query
  // blindly, so a union here would be a lie over whatever the address bar
  // holds. parseWalletRoute narrows it.
  wallet: { tab?: string; method?: string }
  onboarding: Record<string, never>
  "dashboard-settings": Record<string, never>
}

export type ModalName = keyof ModalProps

// Keys the modal itself WRITES into the query, so closing takes them with it.
// Not the keys it reads: `id_afiliado` / `referral` are put there by an
// affiliate campaign and only read by the sign-up form, so stripping them on
// close would drop the attribution from the URL — nothing else stores it.
// The type keeps an entry a subset of that modal's props; it cannot tell
// ownership from readership, so that call is made per key by hand.
export const MODAL_PARAMS: {
  [K in ModalName]: readonly (keyof ModalProps[K] & string)[]
} = {
  "sign-in": ["redirect"],
  "sign-up": ["redirect"],
  wallet: ["tab", "method"],
  onboarding: [],
  "dashboard-settings": []
}

// Fills missing owned query keys and rewrites leftovers (Spanish labels,
// unknown methods) into the English ids the modal actually understands.
// A modal without a canonicalizer leaves the query as the caller wrote it.
export const canonicalizeModalQuery: {
  [K in ModalName]?: (raw: Record<string, string>) => Record<string, string>
} = {
  wallet: canonicalWalletQuery
}

// What a content module may declare about its own shell. The shared chrome
// lives in ModalSurface, so this only carries what is specific to one modal.
export type ModalShellConfig = {
  className?: string
}

export type ModalModule<N extends ModalName> = {
  default: ComponentType<ModalProps[N]>
  shell?: ModalShellConfig
}

const PRODUCTION_MODALS = new Set<ModalName>([
  "sign-in",
  "sign-up",
  "onboarding"
])

export const modalLoaders: {
  [K in ModalName]: () => Promise<ModalModule<K>>
} = {
  "sign-in": () => import("@/features/auth/components/SigninModalContent"),
  "sign-up": () => import("@/features/auth/components/SignupModalContent"),
  wallet: () => import("@/features/wallet/components/WalletModalContent"),
  onboarding: () =>
    import("@/features/onboarding/components/modal/OnboardingModalContent"),
  "dashboard-settings": () =>
    import("@/features/dashboard/components/SettingsModalContent")
}

// One promise per modal, shared by hover prefetch and the use() inside the shell.
const cache = new Map<ModalName, Promise<unknown>>()

// Resolves to the whole module, not just the component: the shell config
// travels in the same chunk, so it is available on the first frame the
// surface renders.
export function loadModal<N extends ModalName>(
  name: N
): Promise<ModalModule<N>> {
  const cached = cache.get(name)
  if (cached) return cached as Promise<ModalModule<N>>

  const pending = modalLoaders[name]()
  // A failed chunk must not stay cached — one offline click would otherwise
  // break the modal for the rest of the session. The rejection still reaches
  // use(), where ModalLoadBoundary turns it into a retry prompt.
  pending.catch(() => cache.delete(name))
  cache.set(name, pending)

  return pending
}

export function invalidateModal(name: ModalName) {
  cache.delete(name)
}

export function prefetchModal(name: ModalName) {
  void loadModal(name)
}

export function isModalName(name: string | null): name is ModalName {
  if (name === null || !Object.hasOwn(modalLoaders, name)) return false
  const modalName = name as ModalName
  if (PRODUCTION_MODALS.has(modalName)) return true
  return isModalDemoEnabled()
}
